<template>
  <div :class="themeClasses.pageContainer" class="modulos-container compact">
    <div class="top-row">
      <div class="title-wrap">
        <h2 class="page-title">
          <i class="fas fa-puzzle-piece me-2"></i>
          Módulos
        </h2>
        <p class="page-subtitle">Gestiona módulos del sistema</p>
      </div>
      <div class="actions">
        <button @click="refreshModules" class="btn btn-sm btn-outline-secondary me-2">
          <i class="fas fa-sync-alt"></i>
        </button>
        <button @click="showCreateModal = true" :class="themeClasses.buttonPrimary" class="btn-sm btn-primary">
          <i class="fas fa-plus"></i>
        </button>
      </div>
    </div>

    <div class="stats-and-filters">
      <div class="stats-inline" v-if="stats">
        <div class="stat small">
          <div class="num">{{ stats.total_modules || 0 }}</div>
          <div class="label">Total</div>
        </div>
        <div class="stat small">
          <div class="num">{{ stats.core_modules || 0 }}</div>
          <div class="label">Core</div>
        </div>
        <div class="stat small">
          <div class="num">{{ stats.custom_modules || 0 }}</div>
          <div class="label">Personal.</div>
        </div>
      </div>

      <div class="filters-inline">
        <input v-model="filters.search" @input="debouncedSearch" type="text" class="form-control form-control-sm search-input" placeholder="Buscar módulos..." />
      </div>
    </div>

    <div class="list-container">
      <div v-if="loading" class="loading-state compact">
        <div class="spinner"></div>
        <div>Cargando módulos...</div>
      </div>

      <div v-else-if="error" class="alert alert-danger">
        {{ error }}
      </div>

      <div v-else-if="!modules || modules.length === 0" class="alert alert-warning">
        No se encontraron módulos. 
        <button @click="loadModules" class="btn btn-sm btn-primary ms-2">Reintentar</button>
      </div>

      <div v-else class="groups-grid">
        <div v-for="(group, gi) in groupedModules" :key="gi" class="group-card">
          <div class="group-parent d-flex justify-content-between align-items-start">
            <div>
              <div class="parent-number">{{ group.parent.code }}</div>
              <div class="parent-name" :title="group.parent.name">{{ group.parent.name || ('Módulo ' + (gi+1)) }}</div>
              <div class="parent-desc">{{ group.parent.description || '' }}</div>
            </div>
            <div class="parent-actions text-end">
              <div class="mt-2">
                <label class="switch-sm">
                  <input v-if="group.parent.id" type="checkbox" :checked="group.parent.is_active" @change="toggleModuleActive(group.parent)" />
                  <span class="slider-sm"></span>
                </label>
              </div>
            </div>
          </div>

          <ul v-if="group.children && group.children.length" class="children-list">
            <li v-for="child in group.children" :key="child.id" class="child-item d-flex justify-content-between align-items-center">
              <div>
                <div class="child-number">{{ child.code }}</div>
                <div class="child-name" :title="child.name">{{ child.name }}</div>
              </div>
              <div class="child-actions d-flex align-items-center gap-2">
                <label class="switch-sm">
                  <input type="checkbox" :checked="child.is_active" @change="toggleModuleActive(child)" />
                  <span class="slider-sm"></span>
                </label>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Modal de Crear/Editar (se mantiene igual) -->
    <div class="modal fade" :class="{ show: showCreateModal }" :style="{ display: showCreateModal ? 'block' : 'none' }" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-puzzle-piece me-2"></i>
              {{ editingModule ? 'Editar Módulo' : 'Nuevo Módulo' }}
            </h5>
            <button type="button" class="btn-close" @click="closeModal"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveModule">
              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label for="moduleCode" class="form-label">Código <span class="text-danger">*</span></label>
                    <input
                      type="text"
                      class="form-control"
                      id="moduleCode"
                      v-model="moduleForm.code"
                      :disabled="editingModule"
                      placeholder="ej: facturacion, inventario"
                      required
                    >
                    <div class="form-text">Código único del módulo (no se puede cambiar)</div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <label for="moduleName" class="form-label">Nombre <span class="text-danger">*</span></label>
                    <input
                      type="text"
                      class="form-control"
                      id="moduleName"
                      v-model="moduleForm.name"
                      placeholder="ej: Módulo de Facturación"
                      required
                    >
                  </div>
                </div>
              </div>
              <div class="mb-3">
                <label for="moduleDescription" class="form-label">Descripción</label>
                <textarea
                  class="form-control"
                  id="moduleDescription"
                  v-model="moduleForm.description"
                  rows="3"
                  placeholder="Descripción del módulo y sus funcionalidades..."
                ></textarea>
              </div>
              <div class="mb-3 form-check">
                <input
                  type="checkbox"
                  class="form-check-input"
                  id="isMain"
                  v-model="moduleForm.is_main"
                >
                <label class="form-check-label" for="isMain">
                  <strong>Módulo Principal</strong>
                  <div class="form-text">Los módulos principales son los módulos padre (sin punto en el código)</div>
                </label>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModal">Cancelar</button>
            <button type="button" class="btn btn-primary" @click="saveModule" :disabled="saving">
              <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
              {{ editingModule ? 'Actualizar' : 'Crear' }} Módulo
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showCreateModal" class="modal-backdrop fade show"></div>
  </div>
</template>

<script>
import api from '@/services/api'
import themeMixin from '@/mixins/themeMixin'
import notificationService from '@/services/notificationService'

export default {
  name: 'ModulosGestion',
  mixins: [themeMixin],
  data() {
    return {
      modules: [],
      stats: null,
      loading: false,
      saving: false,
      error: null,
      showCreateModal: false,
      editingModule: null,
      
      // Filtros
      filters: {
        search: '',
        is_main: ''
      },

      // Formulario
      moduleForm: {
        code: '',
        name: '',
        description: '',
        is_main: false
      },

      // Debounce
      searchTimeout: null
    }
  },
  computed: {
    debouncedSearch() {
      return () => {
        clearTimeout(this.searchTimeout)
        this.searchTimeout = setTimeout(() => {
          this.loadModules()
        }, 500)
      }
    },
    groupedModules() {
      // Database has hierarchical codes: '1', '1.1', '2', '2.1', etc.
      const items = this.modules.map(m => ({ ...m, _code: String(m.code || '') }))

      // Sort by code numerically
      const compareCode = (a, b) => {
        const segA = a._code.split('.').map(s => parseFloat(s) || 0)
        const segB = b._code.split('.').map(s => parseFloat(s) || 0)
        const len = Math.max(segA.length, segB.length)
        for (let i = 0; i < len; i++) {
          const diff = (segA[i] || 0) - (segB[i] || 0)
          if (diff !== 0) return diff
        }
        return 0
      }

      items.sort(compareCode)

      // Group by parent code (those without dots)
      const grouped = []
      const parentMap = new Map()

      const parentCodes = []

      items.forEach(item => {
        if (!item._code.includes('.')) {
          // This is a parent module
          const group = { parent: item, children: [] }
          grouped.push(group)
          parentMap.set(item._code, group)
          parentCodes.push(item._code)
        } else {
          // This is a child module - find its parent
          const parentCode = item._code.split('.')[0]
          const parent = parentMap.get(parentCode)
          if (parent) {
            parent.children.push(item)
          }
        }
      })

      console.log('[DEBUG Modulos] parent codes from dataset:', parentCodes)
      console.log('[DEBUG Modulos] grupos:', grouped.map(g => g.parent.code))

      return grouped
    }
  },
  mounted() {
    this.loadModules()
    this.loadStats()
  },
  
  methods: {
    async loadModules() {
      this.loading = true
      this.error = null
      
      console.log('[ModulosGestion] Iniciando carga de módulos...')
      
      try {
        const params = new URLSearchParams({
          limit: '1000'
        })
        
        if (this.filters.search.trim()) {
          params.append('search', this.filters.search.trim())
        }
        
        console.log('[ModulosGestion] Llamando a API: /modules?' + params.toString())
        const response = await api.get(`/modules?${params}`)
        console.log('[ModulosGestion] Respuesta recibida:', response.data)
        
        if (response.data.success) {
          this.modules = response.data.data
          console.log('[ModulosGestion] Módulos cargados:', this.modules.length, 'primeros 3:', this.modules.slice(0, 3))
        } else {
          throw new Error(response.data.message || 'Error en la respuesta del servidor')
        }
      } catch (err) {
        console.error('[ModulosGestion] Error al cargar módulos:', err)
        this.error = 'Error al cargar los módulos: ' + (err.response?.data?.message || err.message)
      } finally {
        this.loading = false
        console.log('[ModulosGestion] Carga finalizada. loading=false, modules.length=', this.modules?.length)
      }
    },

    async loadStats() {
      try {
        const response = await api.get('/modules?limit=1000')
        if (response.data.success) {
          const allModules = response.data.data
          this.stats = {
            total_modules: allModules.length,
            core_modules: allModules.filter(m => m.is_main).length,
            custom_modules: allModules.filter(m => !m.is_main).length,
            active_modules: allModules.filter(m => m.is_active).length
          }
        }
      } catch (err) {
        console.error('Error al cargar estadísticas:', err)
      }
    },

    refreshModules() {
      this.filters.search = ''
      this.loadModules()
      this.loadStats()
    },

    viewModule(modulo) {
      // Por ahora solo muestra un alert, se puede implementar un modal de detalles
      alert(`Módulo: ${modulo.name}\nCódigo: ${modulo.code}\nDescripción: ${modulo.description || 'Sin descripción'}`)
    },

    editModule(modulo) {
      this.editingModule = modulo
      this.moduleForm = {
        code: modulo.code,
        name: modulo.name,
        description: modulo.description || '',
        is_main: Boolean(modulo.is_main)
      }
      this.showCreateModal = true
    },

    async deleteModule(modulo) {
      const result = await window.Swal.fire({
        title: 'Eliminar Módulo',
        text: `¿Estás seguro de que deseas eliminar el módulo "${modulo.name}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar'
      })

      if (!result.isConfirmed) return

      try {
        const response = await api.delete(`/modules/${modulo.id}`)
        
        if (response.data.success) {
          notificationService.success('Módulo eliminado correctamente')
          this.loadModules()
          this.loadStats()
        } else {
          throw new Error(response.data.message || 'Error al eliminar')
        }
      } catch (err) {
        console.error('Error al eliminar módulo:', err)
        const message = err.response?.data?.message || 'Error al eliminar el módulo'
        notificationService.error(message)
      }
    },

    async saveModule() {
      this.saving = true
      
      try {
        let response
        
        if (this.editingModule) {
          // Actualizar módulo existente
          response = await api.put(`/modules/${this.editingModule.id}`, this.moduleForm)
        } else {
          // Crear nuevo módulo
          response = await api.post('/modules', this.moduleForm)
        }
        
        if (response.data.success) {
          notificationService.success(this.editingModule ? 'Módulo actualizado correctamente' : 'Módulo creado correctamente')
          this.closeModal()
          this.loadModules()
          this.loadStats()
        } else {
          throw new Error(response.data.message || 'Error al guardar')
        }
      } catch (err) {
        console.error('Error al guardar módulo:', err)
        const message = err.response?.data?.message || 'Error al guardar el módulo'
        notificationService.error(message)
      } finally {
        this.saving = false
      }
    },

    closeModal() {
      this.showCreateModal = false
      this.editingModule = null
      this.moduleForm = {
        code: '',
        name: '',
        description: '',
        is_main: false
      }
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('es-ES')
    },

    formatTime(dateString) {
      return new Date(dateString).toLocaleTimeString('es-ES', { 
        hour: '2-digit', 
        minute: '2-digit' 
      })
    },

    async toggleModuleActive(modulo) {
      const newState = !modulo.is_active
      const action = newState ? 'activar' : 'desactivar'
      
      const result = await window.Swal.fire({
        title: `${action.charAt(0).toUpperCase() + action.slice(1)} Módulo`,
        text: `¿Estás seguro de ${action} el módulo "${modulo.name}"?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: newState ? '#28a745' : '#dc3545',
        cancelButtonColor: '#6c757d',
        confirmButtonText: `Sí, ${action}`,
        cancelButtonText: 'Cancelar'
      })

      if (!result.isConfirmed) return

      try {
        const response = await api.put(`/modules/${modulo.id}/toggle-active`, {
          is_active: newState
        })
        
        if (response.data.success) {
          notificationService.success(response.data.message)
          // Recargar módulos para reflejar los cambios
          this.loadModules()
          this.loadStats()
        } else {
          throw new Error(response.data.message || 'Error al cambiar el estado')
        }
      } catch (err) {
        console.error('Error al cambiar estado del módulo:', err)
        const message = err.response?.data?.error || 'Error al cambiar el estado del módulo'
        notificationService.error(message)
      }
    }
  }
}
</script>

<style>
.modulos-container {
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  background-color: var(--secondary-bg-main);
  color: var(--text-primary);
  min-height: 100vh;
}

/* Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border-color);
}

.header-content h1.page-title {
  color: var(--text-primary);
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
}

.page-subtitle {
  color: var(--text-secondary);
  margin: 0.5rem 0 0;
  font-size: 1rem;
  display: flex;
  align-items: center;
}

.btn-primary-custom {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.btn-primary-custom:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  color: white;
}

/* Estadísticas */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.stat-card {
  background: var(--card-bg);
  padding: 1.5rem;
  border-radius: 12px;
  border: 1px solid var(--border-color);
  text-align: center;
  transition: all 0.2s ease;
  color: var(--text-primary);
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--card-shadow-hover);
}

.stat-number {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.stat-label {
  color: var(--text-secondary);
  font-size: 0.875rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.stat-total .stat-number { color: var(--primary-color); }
.stat-core .stat-number { color: var(--danger-color); }
.stat-custom .stat-number { color: var(--text-secondary); }
.stat-active .stat-number { color: var(--success-color); }

/* Filtros */
.filters-section {
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  align-items: center;
  flex-wrap: wrap;
}

.search-box {
  position: relative;
  min-width: 300px;
  flex: 1;
}

/* Asegurar que no haya pseudo-elementos interferentes */
.search-box::before,
.search-box::after,
.search-input::before,
.search-input::after {
  display: none !important;
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.75rem; /* Aumenté el padding izquierdo */
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  background-color: var(--input-bg);
  color: var(--text-primary);
  position: relative;
  z-index: 1;
}

.search-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.search-input::placeholder {
  color: var(--input-placeholder);
}

.search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-secondary);
  font-size: 0.875rem;
  z-index: 2;
  pointer-events: none;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Evitar duplicación de iconos */
.search-box .search-icon:not(:first-child) {
  display: none !important;
}

.filter-group {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.filter-select {
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 0.875rem;
  min-width: 150px;
  background: var(--input-bg);
  color: var(--text-primary);
}

/* Lista de módulos */
.modules-list {
  background: var(--card-bg);
  border-radius: 12px;
  border: 1px solid var(--border-color);
  overflow: hidden;
}

.loading-state, .empty-state {
  padding: 4rem 2rem;
  text-align: center;
  color: var(--text-secondary);
}

.loading-state .spinner {
  width: 3rem;
  height: 3rem;
  border: 3px solid var(--border-color);
  border-top: 3px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state i {
  font-size: 4rem;
  color: var(--text-muted);
  margin-bottom: 1rem;
}

.table {
  margin: 0;
  background-color: var(--card-bg);
  color: var(--text-primary);
}

.table th {
  background-color: var(--secondary-bg-light);
  border-bottom: 1px solid var(--border-color);
  font-weight: 600;
  color: var(--text-primary);
  padding: 1rem;
}

.table td {
  padding: 1rem;
  border-bottom: 1px solid var(--border-color);
  color: var(--text-primary);
}

.table-hover tbody tr:hover {
  background-color: var(--secondary-bg-light);
}

/* Clases de texto específicas para la tabla */
.table .text-dark {
  color: var(--text-primary) !important;
}

.table .text-muted {
  color: var(--text-secondary) !important;
}

.table .text-primary {
  color: var(--primary-color) !important;
}

/* Acciones */
.action-buttons .btn {
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  min-width: 40px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-width: 1px;
  margin: 0 1px;
}

.action-buttons .btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

/* Modal */
.modal {
  z-index: 1050;
}

.modal-backdrop {
  z-index: 1040;
}

.modal-content {
  border: none;
  border-radius: 12px;
  box-shadow: var(--shadow-xl);
  background-color: var(--card-bg);
  color: var(--text-primary);
}

.modal-header {
  border-bottom: 1px solid var(--border-color);
  padding: 1.5rem;
  background-color: var(--card-bg);
}

.modal-body {
  padding: 1.5rem;
  background-color: var(--card-bg);
}

.modal-footer {
  border-top: 1px solid var(--border-color);
  padding: 1.5rem;
  background-color: var(--card-bg);
}

.form-label {
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
}

.form-control {
  border: 1px solid var(--border-color);
  border-radius: 6px;
  padding: 0.75rem;
  transition: all 0.2s ease;
  background-color: var(--input-bg);
  color: var(--text-primary);
}

.form-control:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  background-color: var(--input-bg);
}

.form-control::placeholder {
  color: var(--input-placeholder);
}

.form-text {
  color: var(--text-secondary);
  font-size: 0.75rem;
  margin-top: 0.25rem;
}

.form-check-input:checked {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
}

/* Form check label */
.form-check-label {
  color: var(--text-primary);
}

.form-check-label strong {
  color: var(--text-primary);
}

/* Modal title */
.modal-title {
  color: var(--text-primary);
}

/* Button close */
.btn-close {
  filter: var(--close-button-filter);
}

/* Responsive */
@media (max-width: 768px) {
  .modulos-container { 
    padding: 1rem; 
  }
  
  .page-header { 
    flex-direction: column; 
    gap: 1rem; 
    text-align: center; 
  }
  
  .filters-section { 
    flex-direction: column; 
  }
  
  .filter-group { 
    flex-direction: column; 
    width: 100%; 
  }
  
  .filter-select {
    width: 100%;
  }
  
  .search-box {
    min-width: auto;
  }
  
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 480px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .page-title {
    font-size: 1.5rem;
  }
}

/* === NUEVA VISTA: Grid de módulos con toggle === */

/* Compact stats grid and module cards for admin view */
.top-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.title-wrap .page-title {
  margin: 0;
  font-size: 1.1rem;
}

.title-wrap .page-subtitle {
  margin: 0;
  font-size: 0.85rem;
  color: var(--text-secondary);
}

.stats-and-filters {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-top: 0.5rem;
}

.stats-inline { display:flex; gap:0.6rem; align-items:center }
.stat.small { background:var(--card-bg); border:1px solid var(--border-color); padding:6px 8px; border-radius:6px; text-align:center }
.stat.small .num { font-weight:700; font-size:0.95rem }
.stat.small .label { font-size:0.72rem; color:var(--text-secondary) }

.filters-inline { display:flex; align-items:center }
.filters-inline .search-input { width:220px }

.list-container { margin-top:0.75rem }
.groups-grid { display:grid; grid-template-columns:repeat(3, 1fr); gap:0.75rem }
.group-card { background:var(--card-bg); border:1px solid var(--border-color); padding:0.6rem; border-radius:6px }
.group-parent { gap:0.5rem }
.parent-number { font-weight:700; font-size:0.95rem; color:var(--text-primary) }
.parent-name { font-weight:600; font-size:0.92rem; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:180px }
.parent-desc { font-size:0.78rem; color:var(--text-secondary); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:180px }
.children-list { list-style:none; padding:0; margin:0.4rem 0 0 0 }
.child-item { padding:0.3rem 0; border-top:1px dashed rgba(0,0,0,0.04) }
.child-number { font-size:0.8rem; color:var(--text-secondary); margin-bottom:2px }
.child-name { font-size:0.85rem; font-weight:600; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:160px }
.child-actions .badge { margin-right:4px }

.badge-core { background:linear-gradient(135deg,#f59e0b 0%,#d97706 100%); color:#fff; padding:4px 8px; border-radius:6px; font-size:0.72rem }
.badge-custom { background:linear-gradient(135deg,#8b5cf6 0%,#6d28d9 100%); color:#fff; padding:4px 8px; border-radius:6px; font-size:0.72rem }

.switch-sm { position:relative; display:inline-block; width:40px; height:22px }
.switch-sm input { opacity:0; width:0; height:0 }
.slider-sm { position:absolute; cursor:pointer; top:0; left:0; right:0; bottom:0; background:#ccc; transition:.3s; border-radius:22px }
.slider-sm:before { content:""; position:absolute; height:16px; width:16px; left:3px; bottom:3px; background:#fff; transition:.3s; border-radius:50% }
.switch-sm input:checked + .slider-sm { background:var(--primary-color) }
.switch-sm input:checked + .slider-sm:before { transform:translateX(18px) }

/* Responsive tweaks */
@media (max-width: 992px) {
  .filters-inline .search-input { width:150px }
  .modules-list-compact .desc { max-width:240px }
}

@media (max-width: 576px) {
  .stats-and-filters { flex-direction:column; align-items:stretch }
  .filters-inline { justify-content:space-between }
  .filters-inline .search-input { width:100% }
}

.module-toggle {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.toggle-label {
  font-size: 0.75rem;
  color: var(--text-secondary);
  font-weight: 500;
}

/* Toggle Switch */
.switch {
  position: relative;
  display: inline-block;
  width: 52px;
  height: 28px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 28px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 20px;
  width: 20px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}

input:checked + .slider {
  background-color: var(--primary-color);
}

input:checked + .slider:before {
  transform: translateX(24px);
}

.module-body {
  margin-top: 1rem;
}

.module-description {
  color: var(--text-secondary);
  font-size: 0.9rem;
  line-height: 1.5;
  margin-bottom: 1rem;
}

.module-badges {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.badge-core {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
}

.badge-custom {
  background: linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%);
  color: white;
}

/* Responsive para grid */
@media (max-width: 1024px) {
  .modules-grid {
    grid-template-columns: 1fr;
  }
}
</style>
